import { useMemo } from "react";
import { Input } from "../../../../components/common/input";
import { SectionCard } from "../../../../components/common/section-card";
import { StyledSelect } from "./styled-select";
import { useBrands } from "../../hooks/useBrands";
import { useCategories } from "../../hooks/useCategories";
import { useCountries } from "../../hooks/useCountries";
import { useProductDealTypes } from "../../hooks/useProductDealTypes";
import { useProductScreenSizes } from "../../hooks/useProductScreenSizes";
import { useProductResolutions } from "../../hooks/useProductResolutions";
import type {
  EffectiveFieldConfig,
  FieldKey,
} from "../../../categories/data/field-config";

interface Props {
  name: string;             onNameChange: (v: string) => void;
  brandId: string;          onBrandIdChange: (v: string) => void;
  categoryId: string;       onCategoryIdChange: (v: string) => void;
  sku: string;              onSkuChange: (v: string) => void;
  countryOfOriginId: string; onCountryOfOriginIdChange: (v: string) => void;
  dealTypeId: string;       onDealTypeIdChange: (v: string) => void;
  screenSizeId: string;     onScreenSizeIdChange: (v: string) => void;
  resolutionId: string;     onResolutionIdChange: (v: string) => void;
  notes: string;            onNotesChange: (v: string) => void;
  description: string;      onDescriptionChange: (v: string) => void;
  /** Effective per-category field policy; when omitted every field is shown. */
  fieldPolicy?: EffectiveFieldConfig;
  readOnly?: boolean;
  fieldErrors?: Set<string>;
}

export function BasicInfoSection({
  name, onNameChange,
  brandId, onBrandIdChange,
  categoryId, onCategoryIdChange,
  sku, onSkuChange,
  countryOfOriginId, onCountryOfOriginIdChange,
  dealTypeId, onDealTypeIdChange,
  screenSizeId, onScreenSizeIdChange,
  resolutionId, onResolutionIdChange,
  notes, onNotesChange,
  description, onDescriptionChange,
  fieldPolicy,
  readOnly = false,
  fieldErrors,
}: Props) {
  const err = (key: string) => fieldErrors?.has(key) ? " " : undefined;
  // Policy helpers — default to "visible, optional" when no policy is provided
  // (e.g. before a category is selected) so the form is unchanged.
  const isVisible = (key: FieldKey) => !fieldPolicy || fieldPolicy[key].visible;
  const isRequired = (key: FieldKey) => !!fieldPolicy && fieldPolicy[key].required;
  // Mandatory markers only make sense while the form is editable.
  const markRequired = (required: boolean) => !readOnly && required;
  const { data: brands, isLoading: brandsLoading } = useBrands();
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: countries, isLoading: countriesLoading } = useCountries();
  const { data: dealTypes, isLoading: dealTypesLoading } = useProductDealTypes();
  const { data: screenSizes, isLoading: screenSizesLoading } = useProductScreenSizes();
  const { data: resolutions, isLoading: resolutionsLoading } = useProductResolutions();

  const brandOptions = useMemo(
    () => (brands ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    [brands],
  );
  const categoryOptions = useMemo(
    () => (categories ?? []).map((c) => ({ value: String(c.id), label: c.completeName ?? c.displayName ?? c.name })),
    [categories],
  );
  const countryOptions = useMemo(
    () => (countries ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [countries],
  );
  // Prepend a sentinel "Standard product" option so the seller can opt out of
  // the promotional badge without picking a real deal type. Empty value
  // maps to Odoo's nullable deal_type_id (no badge rendered on the catalog).
  const dealTypeOptions = useMemo(
    () => [
      { value: "", label: "Standard product (no badge)" },
      ...(dealTypes ?? []).map((d) => ({ value: String(d.id), label: d.name })),
    ],
    [dealTypes],
  );
  const screenSizeOptions = useMemo(
    () => (screenSizes ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    [screenSizes],
  );
  const resolutionOptions = useMemo(
    () => (resolutions ?? []).map((r) => ({ value: String(r.id), label: r.name })),
    [resolutions],
  );

  return (
    <SectionCard shadow title="Basic information" className="gap-5">
      <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
        <Input label="Product name" required={markRequired(true)} value={name} onChange={(e) => onNameChange(e.target.value)} placeholder="Enter product name" disabled={readOnly} error={err("name")} />
        <StyledSelect
          label="Brand"
          required={markRequired(true)}
          value={brandId}
          onChange={onBrandIdChange}
          options={brandOptions}
          placeholder="Select brand"
          loading={brandsLoading}
          disabled={readOnly}
          error={err("brandId")}
          searchable
        />
        <StyledSelect
          label="Category"
          required={markRequired(true)}
          value={categoryId}
          onChange={onCategoryIdChange}
          options={categoryOptions}
          placeholder="Select category"
          loading={categoriesLoading}
          disabled={readOnly}
          error={err("categoryId")}
          searchable
        />
        <Input label="SKU" required={markRequired(true)} value={sku} onChange={(e) => onSkuChange(e.target.value)} placeholder="Enter SKU" disabled={readOnly} error={err("sku")} />
        <StyledSelect
          label="Country of origin"
          required={markRequired(true)}
          value={countryOfOriginId}
          onChange={onCountryOfOriginIdChange}
          options={countryOptions}
          placeholder="Select country of origin"
          loading={countriesLoading}
          disabled={readOnly}
          error={err("countryOfOriginId")}
          searchable
        />
        {isVisible("dealType") && (
          <StyledSelect
            label={isRequired("dealType") ? "Deal type" : "Deal type (optional)"}
            required={markRequired(isRequired("dealType"))}
            value={dealTypeId}
            onChange={onDealTypeIdChange}
            options={dealTypeOptions}
            placeholder="Standard product (no badge)"
            loading={dealTypesLoading}
            disabled={readOnly}
            error={err("dealTypeId")}
            searchable
          />
        )}
        {isVisible("screenSize") && (
          <StyledSelect
            label="Screen size"
            required={markRequired(isRequired("screenSize"))}
            value={screenSizeId}
            onChange={onScreenSizeIdChange}
            options={screenSizeOptions}
            placeholder="Select screen size"
            loading={screenSizesLoading}
            disabled={readOnly}
            error={err("screenSizeId")}
            searchable
          />
        )}
        {isVisible("resolution") && (
          <StyledSelect
            label="Resolution"
            required={markRequired(isRequired("resolution"))}
            value={resolutionId}
            onChange={onResolutionIdChange}
            options={resolutionOptions}
            placeholder="Select resolution"
            loading={resolutionsLoading}
            disabled={readOnly}
            error={err("resolutionId")}
            searchable
          />
        )}
        <div className="col-span-2 max-md:col-span-full">
          <Input
            label="Product description"
            required={markRequired(true)}
            multiline
            rows={3}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Enter product description"
            disabled={readOnly}
            error={err("description")}
          />
        </div>
        {isVisible("notes") && (
          <div className="col-span-2 max-md:col-span-full">
            <Input
              label="Internal notes"
              required={markRequired(isRequired("notes"))}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Notes for internal review (not shown to buyers)"
              disabled={readOnly}
            />
          </div>
        )}
      </div>

    </SectionCard>
  );
}
